import { TableBody, Box, Typography, IconButton, CircularProgress, styled } from '@mui/material';
import { CaretRight, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Exam, EXAM_TYPE, ExamModel } from '@shared/types';

import { DataCell, StyledTableRow, SubmissionProgress } from '../Table.styled';
import { Chip, CustomTypography, useFullSkill } from '@shared/ui';
import { formatDatetime } from '@shared/utils';
import { useHomeworkMetadata } from 'hpacademy-data-authentication';

export const StyleExamType = styled(Chip)<{ examType: string }>`
  font-weight: 600;
  font-size: 0.8rem;
  background-color: ${props => (props.examType === EXAM_TYPE.EXERCISE ? '#e2f5ec' : '#ffe4e6')};
  color: ${props => (props.examType === EXAM_TYPE.TEST ? '#10b981' : '#f43f5e')};
`;

interface Props {
  exercises: ExamModel[];
  isLoading: boolean;
  onRowClick?: (exercise: Exam) => void;
}

export const ExercisesManagementTableBody = ({ exercises, isLoading, onRowClick }: Props) => {
  const { dispatcher } = useHomeworkMetadata();
  if (isLoading) {
    return (
      <TableBody>
        <StyledTableRow>
          <DataCell align='center' colSpan={10}>
            <CircularProgress />
          </DataCell>
        </StyledTableRow>
      </TableBody>
    );
  }

  if (!exercises.length) {
    return (
      <TableBody>
        <StyledTableRow>
          <DataCell align='center' colSpan={10}>
            Chưa có bài kiểm tra nào trong lớp này.
          </DataCell>
        </StyledTableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {exercises.map((item, index) => {
        const exam = new Exam(item);

        const totalGraded = exam.testSubmissions.filter(s => s.gradingStatus === 'graded').length;
        const totalSubmissions = exam.testSubmissions.filter(s => s.submissionStatus === 'submitted').length;
        const submissionPercentage = totalSubmissions ? Math.floor((totalGraded / totalSubmissions) * 100) : 0;

        return (
          <StyledTableRow key={exam.id}>
            <DataCell sx={{ fontWeight: 700, color: '#64748b' }}>{index + 1}</DataCell>

            <DataCell
              sx={{
                fontWeight: 600,
                maxWidth: 280,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <StyleExamType examType={exam.type}>{exam?.type || ''}</StyleExamType>
              {exam.title}
              <CustomTypography variant='body2' sx={{ color: '#64748b' }} value={exam.description} />
            </DataCell>

            <DataCell sx={{ fontWeight: 600 }}>{exam.duration} (phút)</DataCell>
            <DataCell>
              <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                {exam.getSkillNames(dispatcher.getSkillNames)}
              </Box>
            </DataCell>

            {/* 4. Assigned Date */}
            <DataCell sx={{ fontWeight: 600 }}>{formatDatetime(item.createdAt)}</DataCell>

            {/* 5. Due Date Indicator */}
            <DataCell>
              {/* <StatusChip label='Quá hạn' statusType='error' size='small' /> */}
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {item.dueDate ? formatDatetime(item.dueDate) : ''}
              </Typography>
            </DataCell>

            {/* 7. Progress Line Metric Bar */}
            <DataCell sx={{ minWidth: 240 }}>
              <Box display='flex' alignItems='center' gap={2}>
                <SubmissionProgress variant='determinate' value={submissionPercentage} isComplete={false} />
                <Box display='flex' alignItems='center' gap={1.5}>
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={0.5}
                    sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.85rem' }}
                  >
                    <CheckCircle />
                    <span>{totalGraded}</span>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={0.5}
                    sx={{ color: '#f43f5e', fontWeight: 700, fontSize: '0.85rem' }}
                  >
                    <XCircle />
                    <span>{String(totalSubmissions - totalGraded)}</span>
                  </Box>
                </Box>
                <IconButton size='small' sx={{ color: '#64748b' }} onClick={() => onRowClick?.(exam)}>
                  <CaretRight />
                </IconButton>
              </Box>
            </DataCell>
          </StyledTableRow>
        );
      })}
    </TableBody>
  );
};
